import Seo from "@/components/Seo";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Leaf } from "lucide-react";
import BrandMark from "@/components/BrandMark";

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [fp, setFp] = useState({ email: "", college: "", batch_year: "", new_password: "", confirm: "" });
  const [fpSaving, setFpSaving] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    if (fp.new_password !== fp.confirm) { toast.error("New passwords don't match"); return; }
    if (fp.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setFpSaving(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: fp.email.trim().toLowerCase(),
        college: fp.college.trim(),
        batch_year: parseInt(fp.batch_year, 10),
        new_password: fp.new_password,
      });
      if (data.token) localStorage.setItem("ayur_token", data.token);
      toast.success("Password reset — you're signed in");
      setFpOpen(false);
      await refresh();
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally { setFpSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--ayur-cream)]">
      <Seo title="Log in" description="Log in to VaidGram — the private network for BAMS, MD & MS Ayurveda scholars." path="/login" />
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Contemporary Ayurveda doctor"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[color:var(--ayur-emerald)]/60" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center text-white"><BrandMark size={48} /></span>
            <span className="font-serif-display text-2xl">VaidGram</span>
          </div>
          <div>
            <div className="label-eyebrow text-[color:var(--ayur-gold)]">the samvad</div>
            <h2 className="font-serif-display text-4xl mt-3 leading-tight max-w-md">
              “Ayurveda is not a memorized science. It is a conversation.”
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form data-testid="login-form" onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="label-eyebrow">Log in</div>
            <h1 className="font-serif-display text-3xl mt-2 text-[color:var(--ayur-emerald)]">Welcome back, scholar</h1>
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              data-testid="login-email-input"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" required autoComplete="email"
              className="bg-white"
            />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input
              data-testid="login-password-input"
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required autoComplete="current-password"
              className="bg-white"
            />
          </div>
          <Button
            type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white py-6 rounded-full"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="flex items-center justify-between text-sm">
            <Dialog open={fpOpen} onOpenChange={setFpOpen}>
              <DialogTrigger asChild>
                <button type="button" data-testid="forgot-password-link"
                  className="text-[color:var(--ayur-terracotta)] font-semibold link-underline">
                  Forgot password?
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[color:var(--ayur-cream)] max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-2xl">Reset your password</DialogTitle>
                  <DialogDescription className="text-[color:var(--ayur-mute)]">
                    Confirm the same email, college and batch year you signed up with, then set a new password. No email required — the details you registered with are the key.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitForgot} className="space-y-3">
                  <div><Label>Email</Label>
                    <Input data-testid="forgot-email-input" type="email" required value={fp.email}
                      onChange={(e) => setFp({ ...fp, email: e.target.value })} className="bg-white" />
                  </div>
                  <div><Label>BAMS College (exactly as signed up)</Label>
                    <Input data-testid="forgot-college-input" required value={fp.college}
                      onChange={(e) => setFp({ ...fp, college: e.target.value })} className="bg-white" placeholder="Government Ayurveda College, Pune" />
                  </div>
                  <div><Label>Batch Year</Label>
                    <Input data-testid="forgot-batch-year-input" type="number" min="1950" max="2035" required
                      value={fp.batch_year} onChange={(e) => setFp({ ...fp, batch_year: e.target.value })} className="bg-white" />
                  </div>
                  <div><Label>New password</Label>
                    <Input data-testid="forgot-new-password-input" type="password" required minLength={6}
                      value={fp.new_password} onChange={(e) => setFp({ ...fp, new_password: e.target.value })} className="bg-white" placeholder="At least 6 characters" />
                  </div>
                  <div><Label>Confirm new password</Label>
                    <Input data-testid="forgot-confirm-password-input" type="password" required minLength={6}
                      value={fp.confirm} onChange={(e) => setFp({ ...fp, confirm: e.target.value })} className="bg-white" />
                  </div>
                  <Button type="submit" disabled={fpSaving} data-testid="forgot-submit-button"
                    className="w-full bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white rounded-full py-6">
                    {fpSaving ? "Resetting…" : "Reset & sign in"}
                  </Button>
                  <p className="text-xs text-[color:var(--ayur-mute)]">
                    Stuck? Email <span className="font-semibold">admin@vaidgram.com</span> and we'll help you back in.
                  </p>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-sm text-[color:var(--ayur-ink-2)]">
            New here? <Link to="/signup" className="text-[color:var(--ayur-terracotta)] font-semibold link-underline">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
